import httpDao from "@/infrastructure/httpDao";

export type CustomerTaskTargetType = "PARENT" | "STUDENT";
export type CustomerTaskStatus = "OPEN" | "COMPLETED";
export type CustomerTaskPriority = "LOW" | "MEDIUM" | "HIGH";
export type CustomerTaskEventAction =
  | "CREATED"
  | "UPDATED"
  | "COMPLETED"
  | "REOPENED"
  | "DELETED";

export type CustomerTaskStaff = {
  id: string;
  name: string;
  email: string;
};

export type CustomerTask = {
  id: string;
  targetType: CustomerTaskTargetType;
  targetId: string | null;
  targetName: string;
  title: string;
  description: string | null;
  status: CustomerTaskStatus;
  priority: CustomerTaskPriority;
  dueDate: string | null;
  facility: { id: string; name: string } | null;
  assignedStaff: CustomerTaskStaff | null;
  createdBy: CustomerTaskStaff | null;
  completedBy: CustomerTaskStaff | null;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
  /** 成人本人への補正前に旧Studentへ記録された、参照専用の履歴。 */
  isLegacySelfHistory?: boolean;
  route: string | null;
  events: Array<{
    id: string;
    action: CustomerTaskEventAction;
    changes: Record<string, unknown> | null;
    createdAt: string;
    actor: CustomerTaskStaff | null;
  }>;
};

export type CustomerTaskAssignee = {
  id: string;
  name: string;
  email: string;
  isCurrent: boolean;
};

export type CustomerTaskFacility = {
  id: string;
  name: string;
};

export type CustomerTaskInput = {
  targetType: CustomerTaskTargetType;
  targetId: string;
  facilityId?: string;
  title: string;
  description?: string;
  priority?: CustomerTaskPriority;
  dueDate?: string;
  assignedStaffUserId?: string;
};

export type CustomerTaskUpdateInput = {
  facilityId?: string | null;
  title?: string;
  description?: string | null;
  priority?: CustomerTaskPriority;
  dueDate?: string | null;
  assignedStaffUserId?: string | null;
};

export const CUSTOMER_TASK_UPDATED_EVENT = "customer-task-updated";

export const notifyCustomerTaskUpdated = () => {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(CUSTOMER_TASK_UPDATED_EVENT));
  }
};

export const customerTaskRepository = {
  findAll: async (
    targetType: CustomerTaskTargetType,
    targetId: string
  ): Promise<CustomerTask[]> => {
    const response = await httpDao.get<CustomerTask[]>("/customer-tasks", {
      params: { targetType, targetId, includeCompleted: true },
    });
    return response.data;
  },

  findForHeader: async (facilityId?: string): Promise<CustomerTask[]> => {
    const response = await httpDao.get<CustomerTask[]>(
      "/customer-tasks/header",
      { params: facilityId ? { facilityId } : undefined }
    );
    return response.data;
  },

  findAssignees: async (
    facilityId?: string
  ): Promise<CustomerTaskAssignee[]> => {
    const response = await httpDao.get<CustomerTaskAssignee[]>(
      "/customer-tasks/assignees",
      { params: facilityId ? { facilityId } : undefined }
    );
    return response.data;
  },

  findTargetFacilities: async (
    targetType: CustomerTaskTargetType,
    targetId: string
  ): Promise<CustomerTaskFacility[]> => {
    const response = await httpDao.get<CustomerTaskFacility[]>(
      "/customer-tasks/target-facilities",
      { params: { targetType, targetId } }
    );
    return response.data;
  },

  create: async (input: CustomerTaskInput): Promise<CustomerTask> => {
    const response = await httpDao.post<CustomerTask>("/customer-tasks", input);
    return response.data;
  },

  update: async (
    id: string,
    input: CustomerTaskUpdateInput
  ): Promise<CustomerTask> => {
    const response = await httpDao.patch<CustomerTask>(
      `/customer-tasks/${id}`,
      input
    );
    return response.data;
  },

  complete: async (id: string): Promise<CustomerTask> => {
    const response = await httpDao.post<CustomerTask>(
      `/customer-tasks/${id}/complete`
    );
    return response.data;
  },

  reopen: async (id: string): Promise<CustomerTask> => {
    const response = await httpDao.post<CustomerTask>(
      `/customer-tasks/${id}/reopen`
    );
    return response.data;
  },

  remove: async (id: string): Promise<void> => {
    await httpDao.delete(`/customer-tasks/${id}`);
  },
};
